"use client";

import { useEffect, useRef } from "react";
import { isDocumentDefined } from "@huub-web/utils";

type MutationObserverCallback = (
  mutations: MutationRecord[],
  observer: MutationObserver,
) => void;

export const useMutationObserver = <T = HTMLElement>(
  callback: MutationObserverCallback,
  options: MutationObserverInit = { childList: true, subtree: true },
) => {
  const ref = useRef<T>(null);
  const observer = useRef<MutationObserver | null>(null);
  const callbackRef = useRef(callback);
  const optionsRef = useRef(options);

  // Update refs when props change
  callbackRef.current = callback;
  optionsRef.current = options;

  useEffect(() => {
    if (!ref.current || !isDocumentDefined()) {
      return;
    }

    const handleMutation: MutationObserverCallback = (mutations, obs) => {
      // Call user callback if provided
      if (callbackRef.current) {
        callbackRef.current(mutations, obs);
      }
    };

    observer.current = new MutationObserver(handleMutation);
    observer.current.observe(
      ref.current as unknown as Node,
      optionsRef.current,
    );

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []); // Empty dependency array - only run once

  return { ref };
};
